import { Component, OnInit, Input, ViewChild, AfterViewInit, SimpleChanges, OnChanges, Output, EventEmitter } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { GoogleMap, MapMarker, MapInfoWindow } from '@angular/google-maps';
import { UntilDestroy } from '@ngneat/until-destroy';

export interface MapMarkerOptions extends google.maps.MarkerOptions {
  data?: any;
}

export interface MapMouseEvent extends google.maps.MapMouseEvent {
  marker: MapMarker;
  data?: any;
}

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    standalone: false
})
@UntilDestroy({ checkProperties: true })
export class MapComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild(GoogleMap, { static: false }) map: GoogleMap;
  @ViewChild(MapInfoWindow, { static: false }) info: MapInfoWindow;

  defaultOptions: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
    zoom: 1,
    draggable: true,
    scrollwheel: true,
    zoomControl: false,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    scaleControl: false
  };

  defaultMarkerIcon: google.maps.Icon = {
    url: '/content/images/map-marker-48.png',
    labelOrigin: new google.maps.Point(25, -5)
  };

  defaultMarkerLabel = {
    color: '#f50a37',
    fontSize: '12px',
    fontWeight: 'bold'
  };

  defaultMarkerAnimation: google.maps.Animation = google.maps.Animation.DROP;

  @Input() width: string;
  @Input() height: string;
  @Input() center: google.maps.LatLngLiteral;
  @Input() options: google.maps.MapOptions = this.defaultOptions;
  @Input() markers: MapMarkerOptions[];
  @Input() simpleMarkers: MapMarkerOptions[];
  @Input() markerIcon: google.maps.Icon;
  @Output() mapClick: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter();
  @Output() markerClick: EventEmitter<MapMouseEvent> = new EventEmitter();
  @Output() markerDblClick: EventEmitter<MapMouseEvent> = new EventEmitter();
  infoContent = '';
  isDoubleClick = false;

  constructor(private logger: NGXLogger) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['markerIcon']) {
      this.markerIcon = Object.assign({}, this.defaultMarkerIcon, this.markerIcon);
    }

    if (changes['options']) {
      this.options = Object.assign({}, this.defaultOptions, this.options);
    }

    if (changes['simpleMarkers']) {
      this.initMarkers();
    }
  }

  gmapClick(event: google.maps.MapMouseEvent): void {
    this.mapClick.emit(event);
  }

  gmarkerClick(event: google.maps.MapMouseEvent, marker: MapMarker, markerOptions: MapMarkerOptions): void {
    this.isDoubleClick = false;
    setTimeout(() => {
      if (!this.isDoubleClick) {
        this.markerClick.emit({ ...event, marker, data: markerOptions.data });
      }
    }, 300);
  }

  gmarkerDblClick(event: google.maps.MapMouseEvent, marker: MapMarker, markerOptions: MapMarkerOptions): void {
    this.isDoubleClick = true;
    this.markerDblClick.emit({ ...event, marker, data: markerOptions.data });
  }

  openInfo(marker: MapMarker, content: string): void {
    this.infoContent = content;
    this.info.open(marker);
  }

  private initMarkers(): void {
    if (this.simpleMarkers) {
      this.markers = [];
      setTimeout(() => {
        this.markers = this.createMarkers(this.simpleMarkers);
      }, 500);
    }
  }

  private createMarkers(simpleMarkers: MapMarkerOptions[]): MapMarkerOptions[] {
    return simpleMarkers.map(simpleMarker => this.createDataMarker(simpleMarker));
  }

  private createDataMarker(maker: MapMarkerOptions): MapMarkerOptions {
    return this.createMarker(
      maker,
      this.options || this.defaultOptions,
      this.markerIcon || this.defaultMarkerIcon,
      this.defaultMarkerAnimation
    );
  }

  private createMarker(
    simpleMarker: MapMarkerOptions,
    defaultOptions: google.maps.MapOptions,
    defaultIcon: google.maps.Icon,
    defaultMarkerAnimation: google.maps.Animation
  ): google.maps.MarkerOptions {
    const isMarkerLabel = (variableToCheck: any): variableToCheck is google.maps.MarkerLabel =>
      (variableToCheck as google.maps.MarkerLabel).text !== undefined;

    const markerTitle = simpleMarker.label && isMarkerLabel(simpleMarker.label) ? simpleMarker.label.text : simpleMarker.label;
    const markerLabel =
      simpleMarker.label && isMarkerLabel(simpleMarker.label)
        ? Object.assign({}, this.defaultMarkerLabel, simpleMarker.label)
        : simpleMarker.label;

    // Workaround for empty string in markerLabel text, will show [Object Object] above pin and not empty text, so we set it to blank instead
    if (simpleMarker.label && isMarkerLabel(simpleMarker.label)) {
      const text = (markerLabel as google.maps.MarkerLabel).text;
      (markerLabel as google.maps.MarkerLabel).text = text === '' ? ' ' : text;
    }

    const markerOptions: MapMarkerOptions = {
      position: simpleMarker.position,
      icon: simpleMarker.icon || defaultIcon,
      label: markerLabel,
      title: simpleMarker.title ?? markerTitle as string,
      animation: simpleMarker.animation || defaultMarkerAnimation,
      data: simpleMarker.data,
      optimized: false
    };

    return markerOptions;
  }
}
